import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Image as ImageIcon, Trash2, Loader2, FolderOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import {
  listPatientDocuments,
  uploadPatientDocuments,
  deletePatientDocument,
  openPatientDocumentInline,
  fetchPatientDocumentBlobUrl,
  DocumentLink,
  PatientDocument,
} from '@/lib/patientDocumentsApi';
import DocumentUploadForm, { DocumentUploadMeta } from './DocumentUploadForm';
import TypeBadge, { TYPE_META } from './TypeBadge';

interface PatientDocumentsProps {
  patientId: string;
  link: DocumentLink; // exactly one of prescriptionId / admissionId, or {} for patient-level
  onCountChange?: (count: number) => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// document_date is a plain 'YYYY-MM-DD' from the API, but defend against a
// full ISO timestamp slipping through (e.g. from an older cached response).
function formatDocumentDate(value: string): string {
  return value.length > 10 ? value.slice(0, 10) : value;
}

// Groups clinical photos by body_site so a doctor sees the before/after/
// follow-up timeline for that site together, chronologically. Non-photo
// documents (and photos without a body_site) fall into their own bucket.
function groupDocuments(docs: PatientDocument[]): { key: string; label: string; docs: PatientDocument[] }[] {
  const groups = new Map<string, PatientDocument[]>();
  for (const doc of docs) {
    const key = doc.document_type === 'clinical_photo' && doc.body_site ? `photo:${doc.body_site}` : `flat:${doc.id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(doc);
  }
  return Array.from(groups.entries()).map(([key, groupDocs]) => ({
    key,
    label: key.startsWith('photo:') ? key.slice('photo:'.length) : '',
    docs: groupDocs,
  }));
}

// Fetches an authenticated blob for an image document and renders it as a
// thumbnail (the endpoint requires a Bearer token, so a plain <img src> to
// the API URL won't work). Owns and revokes its own object URL.
function PhotoThumbnail({ patientId, doc, index, onClick }: { patientId: string; doc: PatientDocument; index: number; onClick: () => void }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let localUrl: string | null = null;
    fetchPatientDocumentBlobUrl(patientId, doc.id).then((u) => {
      if (cancelled) { URL.revokeObjectURL(u); return; }
      localUrl = u;
      setBlobUrl(u);
    }).catch(() => {
      if (!cancelled) setFailed(true);
    });
    return () => {
      cancelled = true;
      if (localUrl) URL.revokeObjectURL(localUrl);
    };
  }, [patientId, doc.id]);

  return (
    <button
      type="button"
      onClick={onClick}
      className="group/thumb relative w-[4.5rem] h-[4.5rem] rounded-md border border-border overflow-hidden bg-muted flex-shrink-0
                 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-medical hover:border-medical-diagnosis/40"
      title={`${doc.file_name} — ${formatDocumentDate(doc.document_date)}`}
    >
      <span className="absolute top-1 left-1 z-10 w-4 h-4 rounded-full bg-black/55 backdrop-blur-sm text-white text-[9px] font-semibold flex items-center justify-center tabular-nums">
        {index + 1}
      </span>
      {blobUrl && !failed ? (
        <img
          src={blobUrl}
          alt={doc.file_name}
          className="w-full h-full object-cover transition-transform duration-300 group-hover/thumb:scale-105"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          {failed
            ? <ImageIcon className="w-5 h-5 text-muted-foreground" />
            : <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        </div>
      )}
      <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/75 to-transparent pt-3 pb-1 px-1">
        <span className="block text-white text-[9px] text-center truncate leading-none tabular-nums">
          {formatDocumentDate(doc.document_date)}
        </span>
      </span>
    </button>
  );
}

export default function PatientDocuments({ patientId, link, onCountChange }: PatientDocumentsProps) {
  const [documents, setDocuments] = useState<PatientDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listPatientDocuments(patientId, link);
      setDocuments(rows);
    } catch (err: any) {
      toast({ title: 'Failed to load documents', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [patientId, link.prescriptionId, link.admissionId]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { onCountChange?.(documents.length); }, [documents, onCountChange]);

  const handleUpload = async (files: File[], meta: DocumentUploadMeta) => {
    setUploading(true);
    try {
      const result = await uploadPatientDocuments(patientId, { ...link, ...meta, files });

      if (result.created.length) {
        toast({ title: `Uploaded ${result.created.length} file${result.created.length > 1 ? 's' : ''}` });
      }
      if (result.errors.length) {
        toast({
          title: `${result.errors.length} file(s) failed`,
          description: result.errors.map(e => `${e.file_name}: ${e.message}`).join('; '),
          variant: 'destructive',
        });
      }
      await refresh();
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (doc: PatientDocument) => {
    try {
      await deletePatientDocument(patientId, doc.id);
      toast({ title: 'Document removed' });
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
    } catch (err: any) {
      toast({ title: 'Failed to remove document', description: err.message, variant: 'destructive' });
    }
  };

  const handleView = async (doc: PatientDocument) => {
    try {
      await openPatientDocumentInline(patientId, doc.id);
    } catch (err: any) {
      toast({ title: 'Failed to open document', description: err.message, variant: 'destructive' });
    }
  };

  const groups = groupDocuments(documents);

  return (
    <div className="space-y-4">
      <DocumentUploadForm onSubmit={handleUpload} submitting={uploading} />

      {/* Document list */}
      {loading ? (
        <div className="text-xs text-muted-foreground flex items-center gap-2 py-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading documents…
        </div>
      ) : documents.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-8 text-center animate-fade-in">
          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
            <FolderOpen className="w-5 h-5 text-muted-foreground" />
          </div>
          <p className="text-xs font-medium text-foreground">No documents attached yet</p>
          <p className="text-[11px] text-muted-foreground max-w-[16rem]">
            Uploaded files will appear here, grouped by body site for clinical photo timelines.
          </p>
        </div>
      ) : (
        <div className="space-y-4 animate-fade-in">
          {groups.map((group) => {
            const isPhotoTimeline = !!group.label && group.docs.every(d => d.mime_type.startsWith('image/'));
            return (
              <div key={group.key}>
                {group.label && (
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="h-px flex-1 bg-border" />
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-medical-diagnosis whitespace-nowrap">
                      {group.label} · {group.docs.length} photo{group.docs.length > 1 ? 's' : ''}
                    </span>
                    <span className="h-px flex-1 bg-border" />
                  </div>
                )}
                {isPhotoTimeline ? (
                  <div className="flex flex-wrap gap-2">
                    {group.docs.map((doc, idx) => (
                      <div key={doc.id} className="relative group">
                        <PhotoThumbnail patientId={patientId} doc={doc} index={idx} onClick={() => handleView(doc)} />
                        <button
                          type="button"
                          onClick={() => handleDelete(doc)}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                          title="Remove"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {group.docs.map((doc) => (
                      <div
                        key={doc.id}
                        className="group flex items-center gap-2.5 rounded-md border border-border bg-card px-2.5 py-2 text-xs
                                   transition-all duration-150 hover:border-info/40 hover:shadow-sm"
                      >
                        <span
                          className="flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center"
                          style={{
                            backgroundColor: `hsl(var(--${TYPE_META[doc.document_type].token}) / 0.1)`,
                            color: `hsl(var(--${TYPE_META[doc.document_type].token}))`,
                          }}
                        >
                          {doc.mime_type.startsWith('image/')
                            ? <ImageIcon className="w-3.5 h-3.5" />
                            : <FileText className="w-3.5 h-3.5" />}
                        </span>

                        <button
                          type="button"
                          onClick={() => handleView(doc)}
                          className="flex-1 min-w-0 flex items-center gap-2 text-left hover:text-info transition-colors"
                        >
                          <span className="truncate font-medium">{doc.file_name}</span>
                          <TypeBadge type={doc.document_type} />
                          <span className="text-muted-foreground flex-shrink-0 tabular-nums">{formatDocumentDate(doc.document_date)}</span>
                          <span className="text-muted-foreground flex-shrink-0 tabular-nums hidden sm:inline">{formatFileSize(doc.file_size)}</span>
                        </button>

                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => handleDelete(doc)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
